/**
 * useNotificationSubscription Hook
 *
 * Real-time subscription for new notifications.
 * Automatically updates the notification cache when new notifications arrive.
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useBusinessContext } from '@/context/BusinessContext';
import { toast } from '@/hooks/ui/use-toast';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';
import { notificationQueryKeys } from './useNotifications';
import type { Notification, NotificationPriority } from '@/types/notifications';
import { PRIORITY_CONFIG, CATEGORY_CONFIG } from '@/types/notifications';

interface UseNotificationSubscriptionOptions {
  showToastOnNew?: boolean;
  toastPriorities?: NotificationPriority[];
}

/**
 * Subscribe to real-time notification updates
 * Shows toast notifications for urgent/action_required by default
 */
export function useNotificationSubscription({
  showToastOnNew = true,
  toastPriorities = ['urgent', 'action_required'],
}: UseNotificationSubscriptionOptions = {}) {
  const { user } = useAuth();
  const { selectedBusinessId } = useBusinessContext();
  const queryClient = useQueryClient();

  // Show toast for new notification
  const showNotificationToast = useCallback((notification: Notification) => {
    if (!showToastOnNew) return;
    if (!toastPriorities.includes(notification.priority)) return;

    const priorityConfig = PRIORITY_CONFIG[notification.priority];
    const categoryConfig = CATEGORY_CONFIG[notification.category];

    // Use appropriate toast based on priority
    if (notification.priority === 'urgent') {
      toast.error(notification.title, {
        description: notification.message || undefined,
      });
    } else if (notification.priority === 'action_required') {
      toast.warning(notification.title, {
        description: notification.message || undefined,
      });
    } else {
      toast.info(notification.title, {
        description: notification.message || undefined,
      });
    }
  }, [showToastOnNew, toastPriorities]);

  // Subscribe to notifications channel
  useEffect(() => {
    if (!user) return;

    // Build filter for this user's notifications
    // We listen to all inserts and filter client-side since complex OR filters
    // aren't fully supported in realtime subscriptions
    const channelName = selectedBusinessId
      ? `notifications-${selectedBusinessId}-${user.id}`
      : `notifications-user-${user.id}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
        },
        (payload) => {
          const newNotification = payload.new as Notification;

          // Check if this notification is relevant to us
          const isForBusiness = selectedBusinessId && newNotification.business_id === selectedBusinessId;
          const isForUser = newNotification.user_id === user.id;

          if (!isForBusiness && !isForUser) return;

          // Skip invalidation if tab just became visible
          if (shouldSkipInvalidation()) return;

          // Invalidate queries to trigger refetch
          queryClient.invalidateQueries({
            queryKey: notificationQueryKeys.list(selectedBusinessId),
          });

          // Show toast for high-priority notifications
          showNotificationToast(newNotification);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
        },
        (payload) => {
          const updatedNotification = payload.new as Notification;

          // Check if this notification is relevant to us
          const isForBusiness = selectedBusinessId && updatedNotification.business_id === selectedBusinessId;
          const isForUser = updatedNotification.user_id === user.id;

          if (!isForBusiness && !isForUser) return;

          // Skip invalidation if tab just became visible
          if (shouldSkipInvalidation()) return;

          // Invalidate queries to update read/dismissed state
          queryClient.invalidateQueries({
            queryKey: notificationQueryKeys.list(selectedBusinessId),
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, selectedBusinessId, queryClient, showNotificationToast]);
}
